import { FunctionComponent, useState } from 'react';
import { Box, TextField } from '@mui/material';
import { MarkdownEditor } from 'src/shared/MarkdownEditor';
import AttachmentUpload from 'src/shared/AttachmentUpload';
import ExpandablePlan from 'src/shared/ExpandablePlan';
import {
    SectionRoot,
    SectionTitle,
    SectionSpacing,
    FeedbackLabel,
    feedbackTextFieldStyles,
    ActionButtonsRow,
    OutlinedActionButton,
    PrimaryActionButton,
    FinalizeButton,
    FinalizeProgress
} from '../../styled';

interface Props {
    plan: string;
    isEditMode: boolean;
    showFeedback: boolean;
    onEdit: () => void;
    onEditCancel: () => void;
    onEditSave: (plan: string, attachmentList?: number[]) => void;
    onFeedbackOpen: () => void;
    onFeedbackCancel: () => void;
    onFeedbackSubmit: (feedback: string, attachmentList?: number[]) => void;
    onFinalize: () => void;
    canFinalize: boolean;
    isLoading: boolean;
    readOnly?: boolean;
}

const PlanReview: FunctionComponent<Props> = ({
    plan,
    isEditMode,
    showFeedback,
    onEdit,
    onEditCancel,
    onEditSave,
    onFeedbackOpen,
    onFeedbackCancel,
    onFeedbackSubmit,
    onFinalize,
    canFinalize,
    isLoading,
    readOnly = false
}) => {
    const [editedPlan, setEditedPlan] = useState(plan);
    const [feedbackText, setFeedbackText] = useState('');
    const [editAttachmentIds, setEditAttachmentIds] = useState<number[]>([]);
    const [feedbackAttachmentIds, setFeedbackAttachmentIds] = useState<
        number[]
    >([]);

    if (isEditMode && !readOnly) {
        return (
            <SectionRoot>
                <SectionTitle>Edit Plan</SectionTitle>
                <Box data-color-mode="dark">
                    <MarkdownEditor
                        value={editedPlan}
                        onChange={(val) => setEditedPlan(val || '')}
                        height={500}
                    />
                </Box>
                <AttachmentUpload
                    attachmentIds={editAttachmentIds}
                    setAttachmentIds={setEditAttachmentIds}
                    compact
                />
                <ActionButtonsRow>
                    <OutlinedActionButton
                        variant="contained"
                        onClick={onEditCancel}
                    >
                        Cancel
                    </OutlinedActionButton>
                    <PrimaryActionButton
                        variant="contained"
                        onClick={() =>
                            onEditSave(editedPlan, editAttachmentIds)
                        }
                    >
                        Save
                    </PrimaryActionButton>
                </ActionButtonsRow>
            </SectionRoot>
        );
    }

    return (
        <SectionRoot>
            <ExpandablePlan plan={plan} />

            {!readOnly && (
                <>
                    {showFeedback && (
                        <SectionSpacing>
                            <FeedbackLabel>Feedback</FeedbackLabel>
                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                placeholder="Describe what should be changed..."
                                value={feedbackText}
                                onChange={(e) =>
                                    setFeedbackText(e.target.value)
                                }
                                variant="outlined"
                                sx={feedbackTextFieldStyles}
                            />
                            <AttachmentUpload
                                attachmentIds={feedbackAttachmentIds}
                                setAttachmentIds={setFeedbackAttachmentIds}
                                compact
                            />
                            <ActionButtonsRow>
                                <OutlinedActionButton
                                    variant="contained"
                                    onClick={onFeedbackCancel}
                                >
                                    Cancel
                                </OutlinedActionButton>
                                <PrimaryActionButton
                                    variant="contained"
                                    disabled={!feedbackText.trim()}
                                    onClick={() => {
                                        onFeedbackSubmit(
                                            feedbackText,
                                            feedbackAttachmentIds
                                        );
                                        setFeedbackText('');
                                        setFeedbackAttachmentIds([]);
                                    }}
                                >
                                    Submit Feedback
                                </PrimaryActionButton>
                            </ActionButtonsRow>
                        </SectionSpacing>
                    )}

                    {!showFeedback && (
                        <ActionButtonsRow>
                            <OutlinedActionButton
                                variant="contained"
                                onClick={onEdit}
                            >
                                Edit Plan
                            </OutlinedActionButton>
                            <OutlinedActionButton
                                variant="contained"
                                onClick={onFeedbackOpen}
                            >
                                Send Feedback
                            </OutlinedActionButton>
                            <FinalizeButton
                                variant="contained"
                                onClick={onFinalize}
                                disabled={isLoading || !canFinalize}
                            >
                                {isLoading ? (
                                    <FinalizeProgress size={20} />
                                ) : (
                                    'Finalize & Create Request'
                                )}
                            </FinalizeButton>
                        </ActionButtonsRow>
                    )}
                </>
            )}
        </SectionRoot>
    );
};

export default PlanReview;
